import {useEffect,useState} from 'react';
import {EmailRecentMessage,EmailSelection,cancelEmailSelection,loadEmailIntakeSettings,loadEmailSelections,loadRecentEmailTimes,selectEmailForIntake} from './api';

/**
 * Human-selected email intake (#975). Staff pick one email they have already read in their own mail
 * program, by its received time; ProFenz shows no sender, subject or content here. Picking only
 * allows that one email to be copied into ProFenz for review. Nothing is approved, replied to or
 * turned into a job from this panel.
 */
const stateLabels:Record<string,string>={
  selected:'Waiting to be copied into review.',
  admitted:'Being copied into review.',
  received:'Copied into ProFenz.',
  reconciliation_required:'Could not be completed. ProFenz staff will follow up.',
  cancelled:'Cancelled before it was read.',
  expired:'Ended before it was read.',
};
const reasonLabels:Record<string,string>={
  authority_lost:'Access changed before it finished.',
  expired_in_flight:'Time ran out before it finished.',
  scope_denied:'The email is no longer in the connected folder.',
  message_gone:'The email is no longer in the mailbox.',
  provider_failure:'Reading the email did not finish (Microsoft, time or storage).',
  content_refused:'The email could not be accepted for review.',
  custody_declined:'Access changed before it was saved.',
  cancelled_in_flight:'Stopped while it was being read.',
};
// Where a copied email actually stands, from ProFenz custody itself. Only needs_review is pending
// review; a copy does not mean a job exists or that the request was accepted.
const sourceLabels:Record<string,string>={
  reserved:'Being saved.',
  storing:'Being saved.',
  needs_review:'Pending review.',
  reconciliation_required:'Saving needs staff follow-up.',
  conversion_pending:'Reviewed; the job is being created.',
  converted:'Reviewed and turned into a job.',
  rejected:'Reviewed and declined.',
};
const sourceLabel=(value:string|null):string=>label(sourceLabels,value)??'Its review status needs confirmation.';
const label=(labels:Record<string,string>,value:string|null):string|undefined=>value!==null&&Object.prototype.hasOwnProperty.call(labels,value)?labels[value]:undefined;
const local=(iso:string):string=>new Date(iso).toLocaleString(undefined,{dateStyle:'medium',timeStyle:'medium'});

export function EmailIntakeSelection({organizationId}:{organizationId:string}){
  const [attempt,setAttempt]=useState(0);
  const [loaded,setLoaded]=useState<{organizationId:string;attempt:number;connections:string[];selections:EmailSelection[];failed:boolean}|null>(null);
  const [recent,setRecent]=useState<{connectionId:string;messages:EmailRecentMessage[]}|null>(null);
  const [notice,setNotice]=useState<string|null>(null);
  const [busy,setBusy]=useState(false);
  useEffect(()=>{
    let active=true;
    const controller=new AbortController();
    void Promise.all([loadEmailIntakeSettings(organizationId,controller.signal),loadEmailSelections(controller.signal)]).then(([settings,selections])=>{
      if(active)setLoaded({organizationId,attempt,selections,failed:false,
        connections:settings.connections.filter(c=>c.provider==='microsoft_365'&&(c.state==='connected'||c.state==='expiring')).map(c=>c.id)});
    },()=>{if(active)setLoaded({organizationId,attempt,connections:[],selections:[],failed:true});});
    return ()=>{active=false;controller.abort();};
  },[organizationId,attempt]);
  const current=loaded?.organizationId===organizationId&&loaded.attempt===attempt?loaded:null;
  const refresh=()=>{setRecent(null);setAttempt(value=>value+1);};
  const failure=(error:unknown,fallback:string)=>setNotice(error instanceof Error&&error.message==='step_up_or_access'
    ?'Confirm your password again, then try once more. If this continues, your account may not have access to pick emails.':fallback);
  const showRecent=async(connectionId:string)=>{
    setBusy(true);setNotice(null);
    try{setRecent({connectionId,messages:await loadRecentEmailTimes(connectionId)});}
    catch{setNotice('Recent emails could not be loaded. Try again shortly.');}
    finally{setBusy(false);}
  };
  const pick=async(connectionId:string,message:EmailRecentMessage)=>{
    setBusy(true);setNotice(null);
    try{
      const result=await selectEmailForIntake(connectionId,message.selectionToken,crypto.randomUUID());
      setNotice(result.outcome==='already_received'?`That email is already in ProFenz. ${sourceLabel(result.sourceState)}`
        :result.outcome==='already_selected'?'That email was already picked.':'Email picked. It will be copied into ProFenz for review shortly.');
      refresh();
    }catch(error){failure(error,'The email could not be picked. Try again.');}
    finally{setBusy(false);}
  };
  const cancel=async(selection:EmailSelection)=>{
    setBusy(true);setNotice(null);
    try{
      const stopped=await cancelEmailSelection(selection.id,selection.version);
      setNotice(stopped.state==='reconciliation_required'?'Reading stopped. ProFenz staff will check what was saved.':'Pick cancelled.');
      refresh();
    }
    catch(error){failure(error,'The pick could not be cancelled. Refresh and try again.');}
    finally{setBusy(false);}
  };
  return <section className="panel" aria-labelledby="email-intake-selection-title">
    <h3 id="email-intake-selection-title">Pick emails for intake review</h3>
    <p>Find the email in your own mail program, then pick it here by the time it arrived. Only the email you pick is copied into ProFenz for review. Nothing is approved or replied to from here.</p>
    {!current&&<p role="status">Loading…</p>}
    {current?.failed&&<p role="status">Email picking is not available right now. Try again, or contact ProFenz support if this continues.</p>}
    {notice&&<p role="status">{notice}</p>}
    {current&&!current.failed&&<>
      {current.connections.length===0?<p>No connected Microsoft 365 mailbox is available.</p>:current.connections.map(connectionId=><div key={connectionId}>
        <button type="button" disabled={busy} onClick={()=>void showRecent(connectionId)}>Show recent email times</button>
        {recent?.connectionId===connectionId&&(recent.messages.length===0?<p>No recent emails are available to pick.</p>:<ul aria-label="Recent email arrival times">{recent.messages.map(message=><li key={message.selectionToken}>
          <span>Arrived {local(message.receivedAt)}</span>{' '}
          <button type="button" disabled={busy} onClick={()=>void pick(connectionId,message)}>Pick this email</button>
        </li>)}</ul>)}
      </div>)}
      <h4>Picked emails</h4>
      {current.selections.length===0?<p>No emails have been picked yet.</p>:<ul aria-label="Picked emails">{current.selections.map(selection=><li key={selection.id}>
        <span>Arrived {local(selection.receivedAt)}: {label(stateLabels,selection.state)??'Status needs confirmation.'}</span>
        {selection.state==='received'&&<span> {sourceLabel(selection.sourceState)}</span>}
        {selection.reason!==null&&<span> {label(reasonLabels,selection.reason)??''}</span>}
        {selection.state==='selected'&&<>{' '}<button type="button" disabled={busy} onClick={()=>void cancel(selection)}>Cancel pick</button></>}
        {selection.state==='admitted'&&<>{' '}<button type="button" disabled={busy} onClick={()=>void cancel(selection)}>Stop reading</button></>}
      </li>)}</ul>}
      <button type="button" disabled={busy} onClick={refresh}>Refresh</button>
    </>}
  </section>;
}
